import { NewReleasePageResponse, PerfectSectionId } from '../models/new-release-page.types';
import {
  BANNED_76_PLATES,
  BANNED_76_WILDCARDS,
  BANNED_76_TOTAL,
  BANNED_76_GROUPS,
} from './banned-76';

// Scaffold for the /new-release/:year page response.
// IMPORTANT: This file MUST NOT contain plate cards, prices, or sold-ticker
// entries. Those are transactional data that must come from real endpoints
// (`?process=new-release&filters=new-{year}-release`, `?process=new-release-name`,
// `/MarketWatch/recently-sold`). Any fake plate or made-up price in this file
// would render on /new-release as if it were real — see #35 for the regression
// where prototype prices like `BL76 AKE £3499` shipped to dev2.
//
// What is allowed here:
//   - Empty `[]` arrays / `null` placeholders that the page service overlays
//     with real fetch results
//   - Banned plates (real, sourced from banned-76.ts FOI file)
//   - Series-level meta that's accurate and not transactional (year, format
//     pattern); copy that depends on facts I cannot verify (released-date,
//     combinations-count) is left empty so the template renders a placeholder
//     rather than a guess
//
// Series 76 fact base (verified 2026-05-13):
//   - DVLA convention: 26 → March 2026, 76 → September 2026
//   - Real sales already on /MarketWatch/recently-sold (pre-orders)

const PERFECT_SECTION_META: Record<PerfectSectionId, { title: string; description: string }> = {
  our_picks: {
    title: 'Our picks.',
    description: 'Our five strongest plates in the release, for appeal and likely value.',
  },
  single_word: {
    title: 'Standouts \u00B7 Words.',
    description: 'Real words spelt in full, with the year read as letters. Fewer than 200 of the 7 million or so plates in a series do this, which is why they are rare and sought after.',
  },
  standout_names: {
    title: 'Standouts \u00B7 Names.',
    description: 'First names and surnames. The flags show the countries where each name is used.',
  },
  standout_foreign: {
    title: 'Standouts \u00B7 Foreign.',
    description: 'Words, brands and places from other languages. The flag shows where each one comes from.',
  },
  standout_vehicles: {
    title: 'Standouts \u00B7 Vehicles.',
    description: 'Car makes, models and motoring words. Most read with the 2 left out, so DA27 SUN reads DATSUN, as DVLA did with DO26 GER for DOGGER.',
  },
  two_word_4_3: {
    title: 'Two words \u00B7 4+3.',
    description: 'Two words that split exactly at the plate\u2019s space, so they read naturally.',
  },
  two_word_3_4: {
    title: 'Two words \u00B7 3+4.',
    description: 'Two words, the first ending inside the year. They read well, though the plate\u2019s space falls inside the second word.',
  },
  two_word_2_5: {
    title: 'Two words \u00B7 2+5.',
    description: 'A two-letter word, then a five-letter word that starts with the year.',
  },
  two_word_5_2: {
    title: 'Two words \u00B7 5+2.',
    description: 'A five-letter word with the year in the middle, then a two-letter word.',
  },
  repeating: {
    title: 'Repeating letters.',
    description: 'The same letter either side of the year. DVLA has held OO \u00B7 OOO back for auction in 49 earlier series.',
  },
  almost_perfect: {
    title: 'Almost perfect.',
    description: 'Longer words with a vowel left out. NO27 HRN reads NORTHERN, with the year as RT.',
  },
};

// Series-level marketing copy. These are factual launch facts (DVLA-published
// release window, combination count, V750 retention term), not transactional
// prices/plates — safe to ship statically. Keyed by yearDisplay so the rest
// of the scaffold stays generic and a future series only adds an entry here.
//
// Series 27 (pre-release, added 2026-10-21): DVLA has put the next March
// series on sale in early November for the last three years (8 Nov 2022,
// 7 Nov 2023, 5 Nov 2024 — first sale rows in soldplate). Until it does,
// `preRelease` switches the page service to the static asset
// assets/data/new-release/27.json: combinations that CAN be made, price TBC,
// auction / banned shown as predictions. No prices, no fake sales. Flip
// `preRelease` to false on release day and the live endpoints take over.
export interface SeriesMeta {
  releasedDate: string;
  releasedLabel?: string;
  combinationsCount: number;
  vehiclesFromDate: string;
  v750Retention: string;
  headlineCopy: string;
  subCopy: string;
  preRelease?: boolean;
  /** Hero photograph (path under src/assets), e.g. '/assets/hero/73.jpg'. */
  heroImage?: string;
  /** Released series curated by the new-release pipeline: sections from the
   *  static asset, live price/availability from plato-search. */
  curated?: boolean;
  ourPicks?: string[];
  digitReading?: string;
  editorsPickCopy?: string;
  editorsPicks?: string[];
  /** Pre-release: ISO instant DVLA's sale opens. */
  saleOpensAt?: string;
  /** Pre-release: signed-out readers see the open half only (draft, see the page). */
  signInGate?: boolean;
  /** Pre-release: ISO instant the fixed-price lists open to members (admins always see them). */
  listsUnlockAt?: string;
}

const SERIES_META: Record<string, SeriesMeta> = {
  '76': {
    heroImage: '/assets/hero/76.jpg',           // BA76 OYS Revuelto, Landmark (owner's photo, 2026-10-25)
    releasedDate: 'May 2026',
    combinationsCount: 7_100_000,
    vehiclesFromDate: '1 September 2026',
    v750Retention: '10 years, renewable',
    headlineCopy: 'The 76 series is out.',
    subCopy: 'Compose a plate and we’ll find it.',
    digitReading: '7 reads as T, I or L; 6 reads as G, B, S or E.',
    editorsPickCopy: 'Sevens turn into Ts and sixes into Gs or Bs, and these three are the cleanest landings: recognisable English on the plate with no character out of place. DVLA held GO76 AME back for auction, its own sign that the plate sits a tier above the rest of the release.',
    editorsPicks: ['NETBALL', 'JETBOAT', 'GO76 AME'],
  },
  // 73 (released Sept 2023): curated from the same pipeline as 27; Our picks chosen with the
  // owner 2026-10-23 on real outcomes (VETERAN £10,429 and ESTELLE £6,333 at DVLA auction,
  // OO73 OOO £3,546, HATEFUL £3,075 with a dealer, NUTELLA £2,391 at auction).
  '73': {
    curated: true,
    heroImage: '/assets/hero/73.jpg',           // NU73 LLA on a Ferrari 812 (owner's pick)
    releasedDate: 'May 2023',
    combinationsCount: 7_100_000,
    vehiclesFromDate: '1 September 2023',
    v750Retention: '10 years, renewable',
    headlineCopy: 'The 73 series.',
    subCopy: '7 reads as T and 3 as E, so 73 reads as TE: the heart of VETERAN, HATEFUL and NUTELLA.',
    digitReading: '7 reads as T and 3 as E, so 73 reads as TE.',
    ourPicks: ['VE73RAN', 'ES73LLE', 'OO73OOO', 'HA73FUL', 'NU73LLA'],
  },
  '27': {
    preRelease: true,
    heroImage: '/assets/hero/27.jpg',
    // No 3+4 plates here: the plate space splits the second word (CA27 EST = CAR T|EST).
    ourPicks: ['OO27OOO', 'PA27NER', 'FO27UNE', 'CA27OON', 'HA27LEY'],
    releasedDate: '4 Nov 2026',          // confirmed by DVLA (dvlapersreg, 2026-10-22)
    saleOpensAt: '2026-11-04T10:00:00Z', // 10:00 UK (GMT in November)
    signInGate: true,
    listsUnlockAt: '2026-11-03T18:00:00Z', // 18:00 UK, the evening before DVLA's sale
    releasedLabel: 'On sale',
    combinationsCount: 7_100_000,
    vehiclesFromDate: '1 March 2027',
    v750Retention: '10 years, renewable',
    headlineCopy: 'The 27 series is on sale.',
    subCopy: 'DVLA put the 27 plates on sale at 10am on Wednesday 4 November. Each plate shows the price DVLA published, or whether DVLA is keeping it back for auction.',
    digitReading: '2 reads as R and 7 as T, so 27 reads as RT, the middle of hundreds of everyday words.',
    editorsPickCopy: 'RT is the richest middle a series has had in years: PARTNER and CARTOON read with no character out of place, and CAR TEST leads a run of car plates (CAR TALK, CAR TECH, CAR TUNE). PART ONE and PART TWO make a matching pair. DVLA has kept PARTNER, CARTOON and CAR TEST back for auction.',
    editorsPicks: ['PA27 NER', 'CA27 OON', 'CA27 EST'],
  },
};

/** Released series curated by the new-release pipeline (platex-valuation-lab
 *  analysis/new_release). Their page meta ships inside the asset
 *  (assets/data/new-release/{year}.json `meta`); SERIES_META above still wins
 *  field-by-field where a series has hand-written copy. */
export const CURATED_SERIES = new Set<string>([
  ...Array.from({ length: 25 }, (_, i) => String(i + 2).padStart(2, '0')),   // 02 … 26
  ...Array.from({ length: 26 }, (_, i) => String(i + 51)),                     // 51 … 76
]);

export function buildScaffold(year: string, assetMeta?: Partial<SeriesMeta>): NewReleasePageResponse {
  const yearNum = parseInt(year, 10);
  const is76 = year === '76';
  const meta: Partial<SeriesMeta> | undefined = (SERIES_META[year] || assetMeta)
    ? { ...(assetMeta || {}), ...(SERIES_META[year] || {}) } : undefined;
  return {
    series: {
      year: isNaN(yearNum) ? 0 : yearNum,
      yearDisplay: year,
      releasedDate: meta?.releasedDate || '',
      combinationsCount: meta?.combinationsCount || 0,
      vehiclesFromDate: meta?.vehiclesFromDate || '',
      v750Retention: meta?.v750Retention || '',
      heroImagePath: meta?.heroImage || (is76 ? '/assets/hero/76.png' : ''),
      headlineCopy: meta?.headlineCopy || '',
      subCopy: meta?.subCopy || '',
      preRelease: !!meta?.preRelease,
      releasedLabel: meta?.releasedLabel || 'Released',
      digitReading: meta?.digitReading || '',
      editorsPickCopy: meta?.editorsPickCopy || '',
      editorsPicks: meta?.editorsPicks || [],
      ourPicks: meta?.ourPicks || [],
      saleOpensAt: meta?.saleOpensAt,
      signInGate: !!meta?.signInGate,
      listsUnlockAt: meta?.listsUnlockAt,
    },
    ticker: {
      mode: 'recently_sold',
      label: `Recently sold \u00B7 ${year} series`,
      entries: [],
    },
    perfectPlates: {
      totalCount: 0,
      sections: [],
    },
    names: {
      totalCount: 0,
      featured: null,
      azCounts: {},
      cards: [],
    },
    banned: is76
      ? {
          totalCount: BANNED_76_TOTAL,
          intro: 'The DVLA withholds combinations considered offensive or unsuitable for display. The 76 series list below is sourced from RegTransfers\u2019 Freedom of Information request to the DVLA.',
          source: 'RegTransfers FOI request',
          plates: BANNED_76_PLATES,
          wildcards: BANNED_76_WILDCARDS,
          groups: BANNED_76_GROUPS,
        }
      : {
          totalCount: 0,
          plates: [],
          wildcards: [],
          groups: [],
        },
    headingToAuction: {
      marqueePicks: [],
      namesAuction: [],
    },
    firstSold: {
      entries: [],
      rushDuration: null,
    },
    faqs: [],
  };
}

/** True when a released series is curated from the new-release pipeline asset. */
export function isCurated(year: string): boolean {
  return !!SERIES_META[year]?.curated || CURATED_SERIES.has(year);
}

/** True when the series is configured as pre-release (see SERIES_META). */
export function isPreRelease(year: string): boolean {
  return !!SERIES_META[year]?.preRelease;
}

export { PERFECT_SECTION_META };
